/*** IMPORTS FROM imports-loader ***/
(function() {
var mp = window.mp;
var exports = undefined;
var define = undefined;
var module = undefined;

/* eslint camelcase: 0, no-console: 0, no-var: 0 */
/* globals _, mp */

/*
 * Provides:
 *  mp.api.Base
 *  mp.api.Middleware
 *
 * Overview:
 *  The Base API class deals with handling requests to the mixpanel
 *  internal API's.  Since we have many more API endpoints internally,
 *  and they are much more dynamic, this class needs to be sufficiently
 *  abstract.
 *
 *  This class exposes only a couple of methods for external code
 *  to call:
 *      - init
 *          Initialize the class, all code using any API object
 *          must call this method before using the API
 *
 *      - update_middleware
 *      - set_middleware
 *          These methods allow you to either append or set a list of
 *          middleware for the API to use.  Update appends middleware to
 *          the end of the middleware chain.
 *
 *      - query
 *      - submit
 *          These methods are the only public method of running queries
 *          with the API.  The only difference is that query fires a GET
 *          request, and submit fires a POST request.
 *
 *  REQUEST/RESPONSE PHASE
 *
 *      When external code calls one of the API request methods, the
 *      following things happen:
 *
 *      1.  Process Middleware
 *          Each middleware object is chained together using each one's
 *          on_request function.  This chaining is done with callbacks
 *          which point to the next on_request function.  In this way,
 *          middleware has the ability to stop the middleware chain, and
 *          also make async requests without breaking the original API
 *          request.
 *
 *      2. Make Request
 *          The last on_request handler is on the base API class which
 *          acts like a Middleware object to keep the interface simple.
 *          The final on_request handler sends a query to the Mixpanel
 *          servers.
 *
 *      3.  Response Phase: Process Middleware
 *          When the Mixpanel servers respond, the Middleware objects
 *          are chained in the same mannor as in the initial step,
 *          except in reverse to reflect the middleware system in
 *          Django.  This means Middleware at the top of the middleware
 *          stack will always be executed first in the request phase,
 *          and last on the response phase.
 *
 *      4.  Return response
 *          At the end of the last middleware phase, the API's
 *          on_response is called which calls the external callback
 *          originally passed in when the query was made.
 *
 *  MIDDLEWARE NOTES
 *
 *      Middleware has some special abilities:
 *
 *          - During the request phase, Middleware can manipulate the
 *          request object in any way.
 *          - During the request phase, Middleware may pass a response
 *          object to the next() callback, which will skip all other
 *          middleware in the request phase and immediately start the
 *          response phase.
 *          - During the response phase, Middleware may modify the
 *          request and response objects.
 *
 */

_.namespace(`mp.api`, function() {
  'use strict';

  var Base = function() {};

  /*
   * This function NEEDS to be called before any API method is called.
   *
   * Subclasses should initialize their state in this method and then
   * call Super
   */
  Base.prototype.init = function(config) {
    this.config = _.extend(this.default_config(), config);

    this._middleware = [];

    return this;
  };

  // this function will set the internal middleware array to a passed
  // in array of middleware.  All the passed in middleware should be
  // full initialized and ready to handle requests.
  Base.prototype.set_middleware = function(middleware) {
    this._middleware = middleware;

    _.each(
      this._middleware,
      function(m) {
        m._api_init(this, this.config);
      },
      this,
    );

    return this;
  };

  // this function will update the internal middleware array with the passed
  // in array of middleware.  All the passed in middleware should be
  // full initialized and ready to handle requests.
  Base.prototype.update_middleware = function(middleware) {
    return this.set_middleware(this._middleware.concat(middleware));
  };

  /*
   * Subclasses should implement this method and return a object
   * filled with any default configuration they need to work.  It is
   * recommended to call superclass.default_config and extend the
   * superclass's default_config with yours.
   */
  Base.prototype.default_config = function() {
    return {
      default_params: {},
      base_url: `/api/2.0/`,
    };
  };

  /*
   * Internal method used by the API
   *
   * This method is responsible for preparing the params object to be
   * sent to the server.  It also updates the params object to contain
   * any default params that should be sent with every request.
   */
  Base.prototype.update_params = function(params) {
    return _.extend({}, this.config.default_params, params);
  };

  /*
   * Internal method used by the API
   *
   * This method is responsible for serializing any oddities in the
   * params object before we send it on to Mixpanel.  For example, it
   * JSON encodes all arrays so they get sent to the server properly.
   */
  Base.prototype.serialize_params = function(params) {
    params = _.extend({}, params);
    for (var key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key] && _.isArray(params[key])) {
        params[key] = $.toJSON(params[key]);
      }
    }
    return params;
  };

  /*
   * This method chains the middleware together and starts the
   * middleware chain.  This method is internal and should never be
   * extended.
   */
  Base.prototype._request_handler = function(request) {
    var index = 0;
    var current = this._middleware[index] || this;

    var next = _.bind(function(req, response) {
      if (response) {
        return this._response_handler(req, response);
      } else {
        index += 1;
        current = this._middleware[index] || this;
        return current.on_request(req, next);
      }
    }, this);
    return current.on_request(request, next);
  };

  /*
   * This method sends the request to the Mixpanel servers.
   *
   * This method allows the API to masquerade as a middleware object.
   * This method is called at the end of the request middleware chain and
   * should never be extended.  If you want to implement an alternate
   * final on_request handler, do it in a Middleware object.
   */
  Base.prototype.on_request = function(request, next) {
    // required explicitly for oauth requests
    const extraParameters = {project_id: mp.report.globals.project_id};
    if (mp.report.globals.workspace_id) {
      extraParameters.workspace_id = mp.report.globals.workspace_id;
    }
    var data = _.extend(extraParameters, request.params);

    const dataType = request.params[`format`] === `csv` ? `text` : `json`;
    var serializedData = this.serialize_params(data);
    var timer_id = mp.utility.timer.start();
    return $.ajax(request.url, {
      type: request.method,
      data: serializedData,
      dataType,
      headers: {
        Authorization: `Session`,
        ...request.headers,
      },
      success(d, text_status, jqxhr) {
        request.query_time = mp.utility.timer.stop(timer_id);
        request.jqxhr = jqxhr;
        jqxhr.request = request;
        next(request, d);
      },
      error: _.bind(function(xhr, text_status) {
        request.query_time = mp.utility.timer.stop(timer_id);
        this._error_handler(request, this._parse_error(xhr, text_status));
      }, this),
    });
  };

  /*
   * This method chains middleware together for the response phase and
   * starts the middleware chain.  This method is internal and should
   * never be extended.
   */
  Base.prototype._response_handler = function(request, response) {
    var index = this._middleware.length - 1;
    var current = this._middleware[index] || this;

    var next = _.bind(function(req, res, cancel) {
      if (!cancel) {
        index -= 1;
        current = this._middleware[index] || this;
        return current.on_response(req, res, next);
      }
    }, this);

    return current.on_response(request, response, next);
  };

  /*
   * The free variable is used to determine whether an api call is user
   * initiated.
   *
   * We consider a request user initiated if it is not chaining off of
   * a previous request.
   *
   * Consider the normal scenario where the user doesn't click
   * anything. The following happens
   *
   * 1) Several ajax requests are sent of by the DOMReady event. These
   * are obviously not user initiated.  To make sure they are marked as
   * not user initiated we check whether or not LoadEventEnd has
   * occured. It will have a value of 0 for these requests.
   *
   * 2) These api requests return, since they are not user initiated we
   * increment the free variable. Some of these requests will spawn
   * more requests. So when those requests are made we check if we have
   * a free request slot. If we do then we decrement the free variable
   * and mark the request as non user initiated. If they don't make a
   * request we have a setTimeout of 500 ms which checks if we have a
   * free variable equal or greater to the value we set it to and
   * decrements it.
   *
   * 3) This pattern repeats as requests are received.
   *
   * If the user makes an action the api request will occur in one of
   * two places.
   *
   * 1) Inside a block with a free request slot.
   *
   * 2) Outside a block with a free request slot.
   *
   * If this happens outside the free request slot free will be 0 and
   * it will be marked as user api request.  If this happens inside a
   * free request slot two things can happen.
   *
   * 1) There is another api request caused by the response that
   * incremented the free variable. In that case the response is marked
   * as non user initiated and not submitted for tracking.
   *
   * 2) There is not another api request caused by the api request and
   * we just accidentally marked a user api request as not caused by
   * the user. This would be horrible if we wanted accurate user marking.
   * However we want accurate api marking and are willing to accept some
   * false positives.
   */
  var free = 0;

  /*
   * This method calls the original user callback.
   *
   * This method allows the API to masquerade as a middleware object.
   * This method is called at the end of the response middleware chain
   * and should never be extended.
   */
  Base.prototype.on_response = function(request, response, _next) {
    // eslint-disable-line no-unused-vars
    if (_.isFunction(request.callback)) {
      request.callback(response, request);
    }
    if (!request.user_called) {
      free += 1;
      var curfree = free;
      setTimeout(function() {
        var int_id = setInterval(function() {
          if (free === curfree) {
            free -= 1;
            clearInterval(int_id);
          } else if (free < curfree) {
            clearInterval(int_id);
          }
        }, 10);
      }, 500);
    }
  };

  /*
   * This method chains middleware together for the error phase and
   * starts the middleware chain.  This method is internal and should
   * never be extended.
   */
  Base.prototype._error_handler = function(request, error_obj) {
    var index = this._middleware.length - 1;
    var current = this._middleware[index] || this;

    var next = _.bind(function(req, err, cancel) {
      if (!cancel) {
        index -= 1;
        current = this._middleware[index] || this;
        current.on_error(req, err, next);
      }
    }, this);

    current.on_error(request, error_obj, next);
  };

  /*
   * This method parses a human readable error message out of the
   * jQuery error callback arguments.
   */
  Base.prototype._parse_error = function(xhr, text_status) {
    var errorData = {error: true};

    // fail early on easy to identify errors
    switch (text_status) {
      case `timeout`:
        return _.extend(errorData, {message: `Request Timeout`});
      case `parsererror`:
        return _.extend(errorData, {message: `JSON Parse Error`});
      case `abort`:
        return _.extend(errorData, {message: `Request Aborted`});
    }

    // try to parse any response text as JSON
    try {
      var json = JSON.parse(xhr.responseText);
      if (json.error) {
        errorData.message = json.error;
      }
    } catch (err) {
      errorData.message = `An unknown error occurred`;
    }

    return errorData;
  };

  /*
   * This method calls the original user callback at the end of a
   * error chain.
   */
  Base.prototype.on_error = function(request, error) {
    if (_.isFunction(request.callback)) {
      request.callback(error, request);
    }
  };

  /*
   * This method creates a request object which is passed through the
   * entire request/response phase.  This method is internal and
   * should never be called/extended.
   */
  Base.prototype._make_request = function(method, endpoint, params, callback, context, headers={}) {
    if (callback && context) {
      callback = _.bind(callback, context);
    }
    //If we are chaining a request or we haven't finished loading this is not
    // a user called api request
    var user_called;
    if (window.performance && window.performance.timing && !window.performance.timing.loadEventEnd) {
      user_called = false;
    } else if (free) {
      free -= 1;
      user_called = false;
    } else {
      user_called = true;
    }
    return {
      method,
      endpoint,
      url: this.config.base_url + (endpoint || ``),
      params: this.update_params(params),
      callback,
      user_called,
      start_time: new Date().getTime(),
      headers,
    };
  };

  /*
   * Use this if a cached result is fine.
   */
  var queries = {};

  // Used when user delete rows;
  Base.prototype.invalidate_cache = function() {
    queries = {};
  };

  Base.prototype.get_cached_query = function(endpoint, params) {
    var query_key = endpoint + JSON.stringify(params);
    var query = queries[query_key];
    if (!query) {
      query = this.query(endpoint, params);
      query.params = $.extend(true, {}, params);
      queries[query_key] = query;
    }
    return query;
  };

  Base.prototype.get_cached_submit = function(endpoint, params, force_query = false) {
    var query_key = endpoint + JSON.stringify(params);
    var query = queries[query_key];
    if (force_query || !query) {
      query = this.submit(endpoint, params);
      query.params = $.extend(true, {}, params);
      queries[query_key] = query;
    }
    return query;
  };

  /*
   * The query method allows external code to start a GET
   * request/response chain for a API object.
   *
   * Params:
   *
   *  endpoint
   *      the endpoint to query (eg. /events/properties/values)
   *
   *  params
   *      a js dictionary of params to pass to the server
   *
   *  callback(response, request)
   *      a function which will receive the result
   *
   *  context
   *      if not null, the callback will be bound to this context
   */
  Base.prototype.query = function(endpoint, params, callback, context, headers={}) {
    return this._request_handler(this._make_request(`get`, endpoint, params, callback, context, headers));
  };

  /*
   * The submit method allows external code to start a POST
   * request/response chain for a API object.
   *
   * Params:
   *  See query() above
   *
   */
  Base.prototype.submit = function(endpoint, params, callback, context, headers={}) {
    return this._request_handler(this._make_request(`post`, endpoint, params, callback, context, headers));
  };

  /*
   * The delete method allows external code to start a DELETE
   * request/response chain for a API object.
   *
   * Params:
   *  See query() above
   *
   */
  Base.prototype.delete = function(endpoint, params, callback, context) {
    return this._request_handler(this._make_request(`delete`, endpoint, params, callback, context));
  };

  /* Development use only!  Never call this function in production
   * code.
   */
  Base.prototype.json_q = function(endpoint, params) {
    this.query(endpoint, params, function(data) {
      console.log(JSON.stringify(data, null, `\t`));
    });
  };

  /* Middleware allows you to easily hook into the request/response
   * phases of a API object.  Middleware can cache, format, and do
   * many other powerful things that would be difficult or messy to
   * include in the base API.
   */
  function Middleware() {}

  // call this function whenever you create an instance of any
  // middleware.  This function should NOT be extended.
  Middleware.prototype.init = function(config) {
    this.config = _.extend(this.default_config(), config);
    this.reset();
    return this;
  };

  /*
   * This function is called by the API when it receives the
   * middleware object.  Each instance of a middleware object is
   * assigned it's corresponding API object and has it's config
   * extended with the API config.
   */
  Middleware.prototype._api_init = function(api, api_config) {
    if (this._api_initialized) {
      return;
    }
    this._api_initialized = true;

    this.api = api;
    this.config = _.extend(this.config, api_config);
  };

  /*
   * This function can be extended, and should return a object of your
   * middleware's default configuration.
   */
  Middleware.prototype.default_config = function() {
    return {};
  };

  /*
   * Middleware should do all of their initialization in this method,
   * such as creating storage and setting instance properties.
   */
  Middleware.prototype.reset = function() {};

  /*
   * Extending classes should handle API requests in this method.
   * Once they have finished they should call next with a (possibly)
   * mutated request object.  Next takes an optional second parameter
   * "response" which will short-circuit the middleware processing.
   *
   * Params:
   *
   *  request
   *      a request object
   *
   *  next(request[, response])
   *      call next once the middleware chain can continue.  If you
   *      call next with a response, the request chain will finish and
   *      the response chain will start with your response object.
   *
   */
  Middleware.prototype.on_request = function(request, next) {
    return next(request);
  };

  /*
   * Extending classes should handle API responses in this method.
   * Once they have finished they should call next with (possibly)
   * mutated request and response objects.  Next takes an optional
   * third parameter "cancel" which if set to true will short-circuit
   * the middleware processing.
   *
   * Params:
   *
   *  request
   *      a request object
   *
   *  response
   *      data returned by the server (and possible modified by other
   *      middleware)
   *
   *  next(request, response[, cancel])
   *      call next once the middleware chain can continue.  If cancel
   *      evaluates to true, the response chain will end right away.
   *
   *      *NOTE* if the response chain is canceled you must be
   *      responsible for calling the users callback
   *      (request.callback)
   *
   */
  Middleware.prototype.on_response = function(request, response, next) {
    next(request, response);
  };

  /*
   * Extending classes should handle API errors in this method.
   * Once they have finished they should call next with (possibly)
   * mutated request and error objects.  Next takes an optional
   * third parameter "cancel" which if set to true will short-circuit
   * the middleware processing.
   *
   * Params:
   *
   *  request
   *      a request object
   *
   *  error
   *      object describing the error which occurred (possibly
   *      modified by other middleware)
   *
   *  next(request, error[, cancel])
   *      call next once the middleware chain can continue.  If cancel
   *      evaluates to true, the error chain will end right away.
   *
   *      *NOTE* if the error chain is canceled you must be
   *      responsible for calling the users callback
   *      (request.callback) with a valid error object
   *
   */
  Middleware.prototype.on_error = function(request, error, next) {
    next(request, error);
  };

  return {
    Base,
    Middleware,
  };
});

}.call(window));